<template>
  <section class="form">
    <card-component title="Capture Project Details" icon="ballot">
      <form @submit.prevent="submit">
        <b-field label="Project Name" horizontal>
          <b-input
            icon="bank"
            v-model="form.projectName"
            placeholder="Full project title"
            name="projectName"
            required
          />
        </b-field>
        <b-field label="Project Id" horizontal>
          <b-input
            icon="lock"
            v-model="form.projectId"
            placeholder="Something short, unique and a little obscure"
            name="projectId"
            required
          />
        </b-field>
      </form>
    </card-component>
    <card-component
      title="Add Schools, Grades and Classes"
      icon="ballot-outline"
    >
      <div v-for="(school, schoolIndex) in form.schools" :key="schoolIndex">
        <b-field label="School Name" horizontal>
          <b-input
            icon="domain"
            v-model="form.schools[schoolIndex].name"
            placeholder="School Name"
            name="schoolName"
            required
          />
          <p class="control">
            <b-button
              icon-right="delete"
              class="button is-danger"
              @click="deleteSchool(schoolIndex)"
            ></b-button>
          </p>
        </b-field>
        <div
          v-for="(grade, gradeIndex) in form.schools[schoolIndex].grades"
          :key="gradeIndex"
          style="margin-left: 5rem;"
        >
          <b-field label="Grade" horizontal>
            <b-input
              icon="account-multiple"
              v-model="form.schools[schoolIndex].grades[gradeIndex].gradeKey"
              placeholder="Grade (must be number)"
              name="grade"
              required
            />
            <p class="control">
              <b-button
                icon-right="delete"
                class="button is-danger"
                @click="deleteGrade(schoolIndex, gradeIndex)"
              ></b-button>
            </p>
          </b-field>
          <div
            v-for="(clss, classIndex) in form.schools[schoolIndex].grades[
              gradeIndex
            ].classes"
            :key="classIndex"
            style="margin-left: 5rem;"
          >
            <b-field label="Class" horizontal>
              <b-input
                icon="book-variant"
                v-model="
                  form.schools[schoolIndex].grades[gradeIndex].classes[
                    classIndex
                  ].classKey
                "
                placeholder="Class"
                name="class"
                required
              />
              <p class="control">
                <b-button
                  icon-right="delete"
                  class="button is-danger"
                  @click="deleteClass(schoolIndex, gradeIndex, classIndex)"
                ></b-button>
              </p>
            </b-field>
          </div>
          <hr />
          <b-field grouped style="margin-left: 10rem; margin-bottom: 2rem;">
            <div class="control">
              <b-button
                type="is-primary"
                @click="addNewEmptyClass(schoolIndex, gradeIndex)"
                >Add New Class</b-button
              >
            </div>
          </b-field>
        </div>
        <b-field style="margin-left: 10rem; margin-bottom: 2rem;">
          <div class="control">
            <b-button type="is-primary" @click="addNewEmptyGrade(schoolIndex)"
              >Add New Grade</b-button
            >
          </div>
        </b-field>
      </div>
      <hr />
      <b-field grouped position="is-left">
        <div class="control">
          <b-button type="is-primary" @click="addNewEmptySchool()"
            >Add New School</b-button
          >
        </div>
      </b-field>
    </card-component>
    <card-component title="Ready to submit" icon="check-box-outline">
      <div v-if="presubmit">
        <b-field label="Project Name" horizontal>
          <div v-html="submission.projectName" />
        </b-field>
        <b-field label="Project Id" horizontal>
          <div v-html="submission.project" />
        </b-field>
        <div
          v-for="(school, schoolIndex) in submission.schools"
          :key="schoolIndex"
        >
          <b-field label="School Name" horizontal>
            <div v-html="school.name" />
          </b-field>
          <div
            v-for="(grade, gradeKey) in school.grades"
            :key="gradeKey"
            style="margin-left: 5rem;"
          >
            <b-field label="Grade" horizontal>
              <div v-html="gradeKey" />
            </b-field>
            <div
              v-for="(clss, classKey) in grade.classes"
              :key="classKey"
              style="margin-left: 5rem;"
            >
              <b-field label="Class" horizontal>
                <div v-html="classKey" />
              </b-field>
            </div>
            <hr />
          </div>
        </div>
      </div>
      <div v-else>
        Click preview to load project configuration. Once you're happy, press
        submit.
      </div>
      <b-field horizontal>
        <b-field grouped>
          <div class="control">
            <b-button
              :type="presubmit ? 'is-primary is-outlined' : 'is-primary'"
              @click="loadConfig()"
              v-html="
                presubmit ? 'Refresh configuration' : 'Preview configuration'
              "
            />
          </div>
          <div class="control">
            <b-button
              :type="
                presubmit
                  ? 'is-primary is-success'
                  : 'is-primary is-success is-outlined'
              "
              @click="submit()"
              :disabled="!presubmit"
              v-html="'Submit'"
            />
          </div>
          <div
            v-if="submitMessage"
            v-html="submitMessage.message"
            :style="submitMessage.submitted ? 'color: green;' : 'color: red;'"
          ></div>
        </b-field>
      </b-field>
    </card-component>
  </section>
</template>

<script>
import CardComponent from "@/components/CardComponent";
import mapValues from "lodash/mapValues";
import { mapState, mapActions } from "vuex";

export default {
  name: "CreateProjectComponent",
  components: {
    CardComponent
  },
  data() {
    return {
      isLoading: false,
      presubmit: false,
      form: {
        projectId: null,
        projectName: null,
        schools: []
      },
      submission: null,
      submitMessage: null
    };
  },
  computed: {
    ...mapState({
      user: "user"
    })
  },
  methods: {
    ...mapActions({
      submitProjectStructure: "adminStore/submitProjectStructure"
    }),
    addNewEmptySchool() {
      return this.form.schools.push({
        grades: []
      });
    },
    addNewEmptyGrade(index) {
      return this.form.schools[index].grades.push({
        classes: []
      });
    },
    addNewEmptyClass(schoolIndex, gradeIndex) {
      return this.form.schools[schoolIndex].grades[gradeIndex].classes.push({});
    },
    deleteSchool(index) {
      return this.form.schools.splice(index, 1);
    },
    deleteGrade(schoolIndex, gradeIndex) {
      return this.form.schools[schoolIndex].grades.splice(gradeIndex, 1);
    },
    deleteClass(schoolIndex, gradeIndex, classIndex) {
      return this.form.schools[schoolIndex].grades[gradeIndex].classes.splice(
        classIndex,
        1
      );
    },
    loadConfig() {
      // RESET
      this.submission = {
        project: this.form.projectId.toUpperCase(),
        projectName: this.form.projectName,
        schools: []
      };

      // CONVERT SCHOOL LEVEL
      for (let schoolIndex in this.form.schools) {
        this.submission.schools.push({
          name: this.form.schools[schoolIndex].name,
          grades: {}
        });

        // CONVERT GRADE LEVEL
        for (let grade of this.form.schools[schoolIndex].grades) {
          this.submission.schools[schoolIndex].grades[
            parseInt(grade.gradeKey)
          ] = {
            classes: {}
          };
          for (let clss of grade.classes) {
            this.submission.schools[schoolIndex].grades[
              parseInt(grade.gradeKey)
            ].classes[clss.classKey] = {};
          }
        }
      }

      this.presubmit = true;
    },
    async submit() {
      this.submitMessage = await this.submitProjectStructure(this.submission);
    },
    reset() {
      this.form = mapValues(this.form, item => {
        if (item && typeof item === "object") {
          return [];
        }
        return null;
      });

      this.$buefy.snackbar.open({
        message: "Reset successfully",
        queue: false
      });
    }
  }
};
</script>
